import { gql, useMutation, useQuery } from '@apollo/client';
import React, { useMemo, useState } from 'react';
import DataTableNew, { DataTableColumn } from '../../components/main/DataTableNew/DataTableNew';
import { Loader2 } from 'lucide-react';
import {
    hasPreviewFeature,
    PreviewFeatureEntry,
    previewFeatures,
} from '../../previewFeatures';

const GET_PREVIEW_FEATURE_ACCESS = gql`
    query getPreviewFeatureAccess($contains: String) {
        UserList(first: 10000, where: { name: { contains: $contains } }) {
            nodes {
                id
                name
                email
                dynamicData {
                    id
                    type
                    ownerId
                    sharedById
                }
            }
        }
    }
`;

const SET_PREVIEW_FEATURE = gql`
    mutation setUserPreviewFeature($targetUserId: String, $featureKey: String, $enabled: Boolean!) {
        User(id: $targetUserId) {
            setPreviewFeature(targetUserId: $targetUserId, featureKey: $featureKey, enabled: $enabled)
        }
    }
`;

type PreviewUser = {
    id?: string | null;
    name?: string | null;
    email?: string | null;
    dynamicData?: Array<PreviewFeatureEntry | null> | null;
};

type PreviewFeatureAccessData = {
    UserList?: { nodes?: Array<PreviewUser | null> | null } | null;
};

const PreviewFeatureAccess = () => {
    const [searchUser, setSearchUser] = useState('');
    const { data, loading, refetch } = useQuery<PreviewFeatureAccessData>(GET_PREVIEW_FEATURE_ACCESS, {
        variables: { contains: searchUser },
    });
    const [setPreviewFeature] = useMutation(SET_PREVIEW_FEATURE);
    const [busyAssignment, setBusyAssignment] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const users = useMemo(() => (
        (data?.UserList?.nodes ?? [])
            .filter((user): user is PreviewUser => !!user?.id)
            .sort((left, right) => (left.name || left.email || '').localeCompare(right.name || right.email || ''))
    ), [data]);

    const setAssignment = async (user: PreviewUser, feature: typeof previewFeatures[number], assigned: boolean) => {
        if (!user.id) return;

        const assignmentKey = `${user.id}:${feature.key}`;
        setBusyAssignment(assignmentKey);
        setError(null);

        try {
            await setPreviewFeature({
                variables: {
                    targetUserId: user.id,
                    featureKey: feature.key,
                    enabled: !assigned,
                },
            });
            await refetch();
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'The preview assignment could not be updated.');
        } finally {
            setBusyAssignment(null);
        }
    };

    const columns: DataTableColumn[] = [
        { id: 'email', name: 'Email', sortable: true },
        ...previewFeatures.map(feature => ({
            id: feature.key,
            name: feature.label,
            width: '160px',
            render: (user: PreviewUser) => {
                const assigned = hasPreviewFeature(user.dynamicData, user.id, feature.key);
                const assignmentKey = `${user.id}:${feature.key}`;
                const busy = busyAssignment === assignmentKey;

                return (
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={assigned}
                            disabled={busy}
                            aria-label={`${feature.label} access for ${user.name || user.email}`}
                            onChange={() => setAssignment(user, feature, assigned)}
                        />
                        {busy && <Loader2 size={14} className="animate-spin" />}
                    </div>
                );
            },
        })),
    ];

    return (
        <div className="flex flex-col flex-1 min-h-0">
            {error && (
                <div className="px-4 py-2 text-sm border-b" style={{ color: 'var(--error)', borderColor: 'var(--border-default)' }}>
                    {error}
                </div>
            )}
            <DataTableNew
                sortingMode="client"
                defaultSort={{ field: 'name', direction: 'asc' }}
                isLoading={loading}
                data={users}
                columns={columns}
                selectable={false}
                onSearchChanged={setSearchUser}
                emptyTitle="No users found"
                emptyDescription="Users will appear after they first sign in"
                persistenceKey="preview-feature-access"
            />
        </div>
    );
};

export default PreviewFeatureAccess;